import { forwardRef } from 'react'

const PALETTE = {
  ink: '#1C1C1A',
  navy: '#183B56',
  gold: '#A88445',
  grey: '#727272',
  lightGrey: '#A6A6A1',
  paper: '#FAFAF8',
}

const CertificateOfAchievement = forwardRef(function CertificateOfAchievement(
  {
    studentName = 'NAKAYIZA KELLY PEACE',
    course = 'Digital Marketing & Paid Advertising',
    grade = 'A+',
    date = '30 June 2026',
    certificateNumber = 'CERT-2026-0058',
    qrCodeUrl,
    signatureUrl = '/signatures/academy-director.png',
    verificationUrl,
    logoUrl = '/Logos/SMA-Logo-Dark.png',
    academyName = 'SYNERGY MULTIMEDIA ACADEMY',
    tagline = 'Shaping Futures',
    directorTitle = 'Academy Director',
  },
  ref
) {
  const W = 297
  const H = 210
  const cx = W / 2
  const effectiveVerificationUrl =
    verificationUrl ||
    `synergymultimediaacademy.com/verify?certificate=${encodeURIComponent(certificateNumber)}`
  const browserVerificationUrl = effectiveVerificationUrl.startsWith('http')
    ? effectiveVerificationUrl
    : `https://${effectiveVerificationUrl}`
  const displayVerificationUrl = effectiveVerificationUrl
    .replace(/^https?:\/\//, '')
    .split('?')[0]
  const effectiveQrCodeUrl =
    qrCodeUrl ||
    `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(browserVerificationUrl)}`

  return (
    <svg
      ref={ref}
      xmlns="http://www.w3.org/2000/svg"
      viewBox={`0 0 ${W} ${H}`}
      width="297mm"
      height="210mm"
      className="sma-certificate"
      style={{ display: 'block', width: '100%', height: 'auto', maxWidth: '297mm', background: PALETTE.paper }}
    >
      <defs>
        {/* Subtle, high-quality paper-grain: fine fractal noise in a warm tone.
            Visible on close inspection, never competes with text. */}
        <filter id="sma-paper-texture" x="-5%" y="-5%" width="110%" height="110%">
          <feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" seed="12" result="noise" />
          <feColorMatrix in="noise" type="matrix" values="0 0 0 0 0.72 0 0 0 0 0.69 0 0 0 0 0.62 0 0 0 .09 0" />
        </filter>
        <pattern id="sma-dot-texture" width="7" height="7" patternUnits="userSpaceOnUse">
          <circle cx="1" cy="1" r="0.18" fill={PALETTE.ink} opacity="0.055" />
        </pattern>
      </defs>

      <rect width={W} height={H} fill={PALETTE.paper} />
      <rect width={W} height={H} filter="url(#sma-paper-texture)" opacity="0.22" />
      <rect width={W} height={H} fill="url(#sma-dot-texture)" opacity="0.14" />

      {/* Deliberately faint geometric texture: it adds depth without becoming ornament. */}
      <g fill="none" stroke={PALETTE.navy} strokeWidth="0.3" opacity="0.09">
        <circle cx={W + 6} cy="-7" r="35" />
        <circle cx={W + 6} cy="-7" r="49" />
        <circle cx={W + 6} cy="-7" r="63" />
        <circle cx="-6" cy={H + 7} r="34" />
        <circle cx="-6" cy={H + 7} r="48" />
        <circle cx="-6" cy={H + 7} r="62" />
      </g>

      <g textAnchor="middle">
        <image href={logoUrl} x={cx - 9} y="11" width="18" height="18" preserveAspectRatio="xMidYMid meet" />
        <text x={cx} y="36" fontSize="3.4" fontFamily="Poppins, Inter, Arial, sans-serif" fontWeight="600" letterSpacing="1.7" fill={PALETTE.ink}>
          {academyName}
        </text>
        <text x={cx} y="42" fontSize="2.5" fontFamily="Poppins, Inter, Arial, sans-serif" letterSpacing="0.35" fill={PALETTE.grey}>
          {tagline}
        </text>
      </g>

      <line x1="18" y1="50" x2={W - 18} y2="50" stroke={PALETTE.ink} strokeWidth="0.45" />

      <g textAnchor="middle">
        <text x={cx} y="81" fontSize="24" fontFamily="Cormorant Garamond, Georgia, Times, serif" fontWeight="500" letterSpacing="1.8" fill={PALETTE.ink}>
          CERTIFICATE
        </text>
        <text x={cx} y="97" fontSize="4.3" fontFamily="Poppins, Inter, Arial, sans-serif" fontWeight="500" letterSpacing="2.1" fill={PALETTE.ink}>
          OF ACHIEVEMENT
        </text>

        <text x={cx} y="109" fontSize="3.5" fontFamily="Poppins, Inter, Arial, sans-serif" fill={PALETTE.grey}>
          This certificate is presented to
        </text>
        <text x={cx} y="134" fontSize="16" fontFamily="Cormorant Garamond, Georgia, Times, serif" fontWeight="600" letterSpacing="0.35" fill={PALETTE.navy}>
          {studentName}
        </text>
        <line x1={cx - 45} y1="140" x2={cx + 45} y2="140" stroke={PALETTE.gold} strokeWidth="0.55" />

        <text x={cx} y="153" fontSize="3.4" fontFamily="Poppins, Inter, Arial, sans-serif" fill={PALETTE.grey}>
          has successfully completed the training course
        </text>
        <text x={cx} y="164" fontSize="5.8" fontFamily="Poppins, Inter, Arial, sans-serif" fontWeight="600" fill={PALETTE.ink}>
          {course}
        </text>
        <text x={cx} y="173" fontSize="3.2" fontFamily="Poppins, Inter, Arial, sans-serif" fill={PALETTE.grey}>
          with a final grade of
        </text>
        <text x={cx} y="183" fontSize="8" fontFamily="Cormorant Garamond, Georgia, Times, serif" fontWeight="700" fill={PALETTE.gold}>
          {grade}
        </text>
      </g>

      <line x1="18" y1="189" x2={W - 18} y2="189" stroke={PALETTE.lightGrey} strokeWidth="0.3" />

      <g fontFamily="Poppins, Inter, Arial, sans-serif" textAnchor="middle">
        <line x1="24" y1="198" x2="76" y2="198" stroke={PALETTE.ink} strokeWidth="0.35" />
        <text x="50" y="202" fontSize="3" fill={PALETTE.grey}>{date}</text>
        <text x="50" y="205" fontSize="2.7" letterSpacing="1" fill={PALETTE.ink}>DATE</text>

        <line x1="221" y1="198" x2="273" y2="198" stroke={PALETTE.ink} strokeWidth="0.35" />
        {signatureUrl && (
          <image
            href={signatureUrl}
            x="223"
            y="187"
            width="48"
            height="11"
            preserveAspectRatio="xMidYMid meet"
          />
        )}
        <text x="247" y="205" fontSize="2.7" letterSpacing="0.7" fill={PALETTE.ink}>{directorTitle.toUpperCase()}</text>

        <text x={cx} y="197" fontSize="3" fontWeight="600" letterSpacing="0.55" fill={PALETTE.ink}>{certificateNumber}</text>
        <text x={cx} y="203" fontSize="2.4" fill={PALETTE.grey}>Verify at {displayVerificationUrl}</text>
      </g>

      <g transform={`translate(${W - 33}, 192)`}>
        <rect width="12" height="12" fill={PALETTE.paper} stroke={PALETTE.lightGrey} strokeWidth="0.25" />
        <image href={effectiveQrCodeUrl} x="0.7" y="0.7" width="10.6" height="10.6" preserveAspectRatio="xMidYMid meet" />
      </g>
    </svg>
  )
})

export default CertificateOfAchievement
